import { z } from 'zod';

/**
 * What an uploaded file looks like, as the planner is allowed to see it.
 *
 * The import design's rule is that the model asks and proposes and never reads
 * rows (2026-09-06 import note §1.2). Code opens the container and writes this
 * document; the planner sees this document, the office's one-line description
 * of the file, and nothing else. So everything here is a *shape* — how full a
 * column is, what kind of value it holds, which values repeat and how often,
 * three examples, which rows are not people — and nothing here is a row.
 *
 * Two constraints govern the shape and are worth stating before the fields:
 *
 *  - **It has to fit in a prompt.** The 71-column board is the size that
 *    matters and it has to come in around 10-20k tokens, so every list is
 *    capped and every value is truncated. A column with more than
 *    `PROFILE_DISTINCT_LIMIT` distinct values reports the count and no values:
 *    that is the difference between a status column, whose whole vocabulary is
 *    useful, and a notes column, whose values are the file.
 *  - **It has to be enough to write the plan against.** The plan contract's
 *    `source.exclusions` list and its per-column targets are chosen from this
 *    document alone, which is why exclusions are counted here rather than
 *    discovered at preview, and why a column of names carries its ten
 *    most-referenced values — that list is how a sentinel row like
 *    `zzz Cold Outreach` cannot be missed (§1.17).
 */

/** How many distinct values a column reports before it reports only the count. */
export const PROFILE_DISTINCT_LIMIT = 40;
/** How many of a name column's most-referenced values are listed. */
export const PROFILE_MOST_REFERENCED_LIMIT = 10;
/** Every value in the profile is truncated to this many characters. */
export const PROFILE_VALUE_MAX_LENGTH = 80;
/** How many example values each column carries. */
export const PROFILE_SAMPLE_COUNT = 3;
/** How many values a column shows for each shape check its names fail. */
export const PROFILE_BIN_EXAMPLE_COUNT = 3;

/**
 * **What the bytes arrived in.** Three of the four are files an office uploads
 * and a parser reads.
 *
 * `dossier` is the fourth and it is not one of those: it is the container an
 * operator's `dossier:import` run writes on its own session row, so that the
 * package files it read are one unit of undo like any other import
 * (`docs/operator-tools.md` §14). Nothing parses it — a package is hand-written
 * JSON validated against the fact catalog before a connection is opened, not
 * bytes profiled into columns — and `parseFile` refuses it by name rather than
 * falling through to the PDF reader.
 */
export const ImportContainer = z.enum(['csv', 'xlsx', 'pdf', 'dossier']);
export type ImportContainer = z.infer<typeof ImportContainer>;

/**
 * What a cell holds. `empty` is its own answer rather than a null type: a
 * column that is 4% full is a different proposition from one that is 90% full,
 * and the fill rate is read off this.
 */
export const CellType = z.enum(['string', 'number', 'date', 'boolean', 'empty']);
export type CellType = z.infer<typeof CellType>;

/**
 * The type the column reports. `mixed` is honest rather than a failure: the
 * board's summary rows put `0/236` in a column of dates, and a planner that is
 * told the column is a date column would write a transform that throws.
 */
export const ColumnType = z.enum(['string', 'number', 'date', 'boolean', 'empty', 'mixed']);
export type ColumnType = z.infer<typeof ColumnType>;

/**
 * The shape checks a candidate name can fail — the reason a row goes to the bin
 * instead of becoming a person.
 *
 * The names live here rather than in the server because both sides read them:
 * the profile carries a count per rule per column, the plan carries the office's
 * decision about them, and the dashboard shows the office the word.
 */
export const BinRule = z.enum([
  /** Nothing but initials: `J. R.` */
  'initials_only',
  /** More than one token, but the last is an initial or a suffix: `Bram M.` */
  'no_last_name',
  /** One word and no more: `Quinnie` */
  'single_token',
  'looks_like_email',
  'looks_like_url',
  /** An emoji or a decoration glyph in the name. */
  'decorated',
  /** A company, not a person: `Marrowstone Holdings, LLC` */
  'looks_like_org',
  /**
   * Two people under one heading that could not be split into two names. Alone
   * among the rules it does not say the string names nobody, so a document
   * heading holding one is still counted as a record — see
   * `DocumentProfile.heading_count`.
   */
  'unsplit_couple',
]);
export type BinRule = z.infer<typeof BinRule>;

export const ValueCount = z.object({
  value: z.string(),
  count: z.number().int().nonnegative(),
});
export type ValueCount = z.infer<typeof ValueCount>;

/**
 * The rules that say a row is not a person. The names are the plan contract's
 * (`ImportPlan.source.exclusions`) so the planner can carry a rule straight
 * through without renaming it.
 */
export const ExclusionRule = z.enum([
  /** Lines above the header — a title, a blurb, a blank. */
  'leading_lines',
  /** The header again, once per group, as monday's export writes it. */
  'repeated_header',
  /** A group title sitting alone in the first column. */
  'single_cell_row',
  /** A row with values but no name: the per-group aggregate row. */
  'nameless_row',
  /** Nothing in it at all. */
  'blank_row',
  /** A row that stands for a channel rather than a person (`zzz Cold Outreach`). */
  'sentinel_name',
  /** A row carrying only a date: a withdrawn LinkedIn profile. */
  'date_only_row',
]);
export type ExclusionRule = z.infer<typeof ExclusionRule>;

export const ProfileExclusion = z.object({
  rule: ExclusionRule,
  /** What was matched, in the office's words — shown next to the count. */
  detail: z.string(),
  count: z.number().int().nonnegative(),
  /** The first few row numbers, so a person can go and look. */
  rows: z.array(z.number().int().positive()),
});
export type ProfileExclusion = z.infer<typeof ProfileExclusion>;

export const ColumnProfile = z.object({
  /** 1-based, matching the cell locator and the spreadsheet's own column numbers. */
  index: z.number().int().positive(),
  header: z.string(),
  /** Share of data rows with something in this column, 0 to 1, two decimals. */
  fill_rate: z.number().min(0).max(1),
  type: ColumnType,
  distinct_count: z.number().int().nonnegative(),
  /** Every value with its count, or null when there are more than the limit. */
  distinct_values: z.array(ValueCount).nullable(),
  samples: z.array(z.string()),
  max_length: z.number().int().nonnegative(),
  /** Cells holding more than one line — the dated-note logs live here. */
  multiline_count: z.number().int().nonnegative(),
  /**
   * True when the column's values read as people's names. It is what turns a
   * free-text column into a relationships column for the planner.
   */
  looks_like_names: z.boolean(),
  /**
   * For a name column, the ten values named most often, comma-separated cells
   * split first. Null on every other column.
   */
  most_referenced: z.array(ValueCount).nullable(),
  /**
   * **Cells this column holds that name two people** (§1.16a) — the ones
   * `splitCoupleHeading` reads as a couple, which is exactly the set the
   * `couple_cell` split rule acts on.
   *
   * A count and never an absence: the planner proposes the split off it, the
   * profile report says it for the name column, and a column with none says
   * zero rather than nothing, so "no couples here" and "nobody counted" are not
   * the same reading.
   *
   * `.default(0)` for the profiles **already in the column**, and that is the
   * whole of what it buys: this shape is stored jsonb, a session profiled before
   * this deploy carries no such count, and a required key would make those rows
   * unreadable rather than make them say zero. For exactly those profiles the
   * two readings collapse — "this column holds no couples" and "nobody counted
   * this column" both come back as 0 — which is why the default is not a
   * substitute for re-profiling a session whose plan turns on this number.
   */
  couple_cells: z.number().int().nonnegative().default(0),
  /**
   * **Cells this column holds that name several organizations** (§1.16b) — the
   * ones `splitOrgList` reads as two or more, which is exactly the set the
   * `split_orgs` transform acts on.
   *
   * A **shape** count and target-blind, exactly as `couple_cells` is: it is
   * taken on every column of every sheet, before anything knows what the column
   * is for. A surname-first name column ("Smith, John") therefore counts, and
   * that is not a defect — the planner is what keeps the transform to
   * organization columns, and a count that only existed where somebody had
   * already decided the column was an employer could never have *suggested* the
   * transform in the first place.
   *
   * `.default(0)` for the profiles **already in the column**, on the same terms
   * `couple_cells` carries it: this shape is stored jsonb, a session profiled
   * before this deploy carries no such count, and a required key would make
   * those rows unreadable rather than make them say zero. For exactly those
   * profiles "this column holds no compound cells" and "nobody counted this
   * column" both come back as 0, which is why the default is not a substitute
   * for re-profiling a session whose plan turns on this number.
   */
  org_list_cells: z.number().int().nonnegative().default(0),
  /**
   * **Cells holding a word written without its spaces** — "DelMarCountryClub",
   * one word with capitals inside it and no space in the cell piece. What the
   * `respace` transform is for, and the evidence the planner needs to propose
   * it: the 2026-09-15 board wrote a whole column this way and nothing said so.
   * Shape-only and target-blind like the two counts above; a brand spelled in
   * capitals inside ("PayPal") counts too, which is why the transform is the
   * planner's choice per column and not a rule per value. `.default(0)` on the
   * same stored-jsonb terms.
   */
  run_together_cells: z.number().int().nonnegative().default(0),
  /**
   * **Cells gluing a place to a name with no space** — "LakesideCountryClub,Houston",
   * "PiedmontDrivingClub.GA". What `place_suffix` reads. `.default(0)` on the
   * same terms.
   */
  glued_place_cells: z.number().int().nonnegative().default(0),
  /**
   * For a name column, the bin as it would be if the column were committed
   * as it stands: the shape check each value fails, and how many fail it. Only
   * the rules that fired are listed. Null on every other column.
   *
   * This is what turns `the office will have to look at some of these` into a
   * number the plan can be written against — how many rows of this column are
   * an address, a company or one word, before anybody commits anything.
   */
  bin_counts: z.partialRecord(BinRule, z.number().int().nonnegative()).nullable(),
  /** Up to three of the values behind each rule, so the number has a face. */
  bin_examples: z.partialRecord(BinRule, z.array(z.string())).nullable(),
  /**
   * **What the cells of a prose column begin with**, top three, with counts —
   * the leading word or two before a colon, where enough cells carry one.
   *
   * One column holding two things is the shape §1.12 was written for: the
   * earlier tool pasted whole dossiers behind a `Dossier:` prefix into the same
   * cell staff type notes in, and a plan that reads all of it as notes pays to
   * extract somebody else's research. The rule was in the prompt from the start
   * — route by the prefix — but the planner had no way to *see* a prefix: it
   * gets three samples of a 4,000-character cell, and three samples of anything
   * says nothing about a distribution. The board's second sheet was recognised
   * only because a human had already described it.
   *
   * So the profile counts them. Empty on a column whose cells begin with nothing
   * in common, which is most of them, and null on a column that is not prose.
   */
  common_prefixes: z.array(ValueCount).nullable(),
});
export type ColumnProfile = z.infer<typeof ColumnProfile>;

/**
 * A row that is a heading for the rows under it: a monday board writes one per
 * group, alone in the first column.
 *
 * These carry the file's own classification — on the contacts board the group a
 * row sits in is what says whether the person is a member, a prospect or
 * declined — so they have to reach the planner, which until now they did not:
 * they were counted as `single_cell_row` exclusions and the words were dropped.
 */
export const GroupTitle = z.object({
  /** 1-based row the title sits on. */
  row: z.number().int().positive(),
  title: z.string(),
  /** Rows that stand for a person under it, up to the next title. */
  data_row_count: z.number().int().nonnegative(),
});
export type GroupTitle = z.infer<typeof GroupTitle>;

export const SheetProfile = z.object({
  name: z.string(),
  /** 0-based position in the workbook; a CSV has exactly one sheet, index 0. */
  index: z.number().int().nonnegative(),
  row_count: z.number().int().nonnegative(),
  column_count: z.number().int().nonnegative(),
  /** 1-based row number of the header, or null when no header was found. */
  header_row: z.number().int().positive().nullable(),
  headers: z.array(z.string()),
  /** Rows left after every exclusion — the rows that stand for a person. */
  data_row_count: z.number().int().nonnegative(),
  columns: z.array(ColumnProfile),
  exclusions: z.array(ProfileExclusion),
  /** The group titles in the sheet, in order, with what sits under each. */
  group_titles: z.array(GroupTitle),
});
export type SheetProfile = z.infer<typeof SheetProfile>;

/**
 * One heading in a document, addressable.
 *
 * Every heading is here, not a sample: a few hundred entries is small next to a
 * profile's other lists, and without them a plan cannot say *which* bio block
 * it means. `heading_samples` stays for the model-facing summary.
 */
export const DocumentHeading = z.object({
  /** 0-based position among the document's headings. */
  index: z.number().int().nonnegative(),
  text: z.string(),
  page: z.number().int().positive(),
  /** Where the heading's block is: the page, then its position in the document. */
  locator: z.object({
    page: z.number().int().positive(),
    block: z.number().int().nonnegative(),
  }),
  /** The two names a couple heading splits into, or null when it is one name. */
  split: z.tuple([z.string(), z.string()]).nullable(),
});
export type DocumentHeading = z.infer<typeof DocumentHeading>;

export const DocumentProfile = z.object({
  page_count: z.number().int().nonnegative(),
  /**
   * Headings that name somebody — a record. A heading that fails the bin's own
   * shape checks is not one: the bios document opens with a URL set at heading
   * size, and counting it as a record told the planner there was one more bio
   * in the file than there is.
   *
   * The exception is a heading the bin holds as `unsplit_couple`. That rule
   * does not say the string is nobody's name, it says it is *two* people and
   * the split rule will not guess which words belong to whom. It is an entry
   * with a bio under it, so it is counted here and the office settles the two
   * names.
   */
  heading_count: z.number().int().nonnegative(),
  /**
   * The headings that name nobody: the URL, the section label, the stray. An
   * unsplit couple heading is not one of these — it names two people.
   */
  non_name_headings: z.number().int().nonnegative(),
  body_block_count: z.number().int().nonnegative(),
  /** Every heading, addressable, records and not. */
  headings: z.array(DocumentHeading),
  /** The first few headings, so the planner can see what a record looks like. */
  heading_samples: z.array(z.string()),
  /** Headings holding `&` or `and` — the couples the split rule is for. */
  conjunction_heading_count: z.number().int().nonnegative(),
  /** Words per body block, so the planner knows whether a block is a bio. */
  body_words: z.object({
    min: z.number().int().nonnegative(),
    median: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
  }),
  /**
   * True when the parser could not separate headings from body by point size
   * and fell back to one block per page. The planner is told rather than shown
   * a document that quietly has no records in it.
   */
  size_split_found: z.boolean(),
});
export type DocumentProfile = z.infer<typeof DocumentProfile>;

export const ImportProfile = z.object({
  version: z.literal(1),
  container: ImportContainer,
  /** One entry per sheet; empty for a PDF. */
  sheets: z.array(SheetProfile),
  /** Present for a PDF, null for a grid. */
  document: DocumentProfile.nullable(),
});
export type ImportProfile = z.infer<typeof ImportProfile>;
